import React from "react";
import { Link } from "react-router-dom";
import { 
  GiftIcon, 
  TreeIcon, 
  SmileyMehIcon, 
  QuestionIcon, 
  DeviceMobileIcon, 
  SmileyNervousIcon,
  LockIcon,
  TargetIcon,
  ConfettiIcon,
  CalendarXIcon
} from "@phosphor-icons/react";
import PublicNavigation from "../components/PublicNavigation";
import PublicFooter from "../components/PublicFooter";
import { usePageMeta } from "../hooks/usePageMeta";
import "./homepage.css";

const HomePage: React.FC = () => {
  // Aggiungi classe per rimuovere padding dal main
  React.useEffect(() => {
    document.body.classList.add("homepage-route");
    return () => {
      document.body.classList.remove("homepage-route");
    };
  }, []);

  // SEO Meta Tags
  usePageMeta({
    title: "CosaVorrei - Liste regalo condivise per gruppi di amici e famiglie",
    description: "Crea e condividi la tua lista dei desideri per Natale, compleanni o matrimoni. Organizza i regali con il tuo gruppo in modo semplice e senza spoiler.",
    ogTitle: "CosaVorrei - Liste regalo per la tua famiglia",
    ogDescription: "Rendere i regali più semplici, una famiglia alla volta.",
    ogUrl: "https://www.cosavorrei.it",
    ogImage: "https://www.cosavorrei.it/og-image.png",
    ogLocale: "it_IT",
    ogImageWidth: "1200",
    ogImageHeight: "630",
    twitterImage: "https://www.cosavorrei.it/og-image.png",
    twitterUrl: "https://www.cosavorrei.it",
    canonicalUrl: "https://www.cosavorrei.it",
  });

  return (
    <div className="homepage">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="hero-section">
        <h1 className="hero-title">
          Ogni desiderio ha il suo posto,<br />
          <span className="highlight">ma solo chi ami può leggerlo.</span> 
        </h1>
        <p className="hero-subtitle">
          Ogni regalo è personale, ma il bello è vivere l'evento insieme.<br/>
          Crea il tuo gruppo e scopri quanto può essere magico organizzarsi così.
        </p>
        <div className="hero-buttons">
          <Link to="/create-group" className="hero-btn primary">
            Crea il tuo gruppo
          </Link>
          <Link to="/login" className="hero-btn secondary">
            Unisciti a un gruppo
          </Link>
        </div>
        <div className="hero-highlight">
          <p className="hero-highlight-text">
            <GiftIcon size={20} weight="regular" /> <strong>Un posto solo per ogni evento:</strong> per organizzare Natale, compleanni e ricorrenze senza spoiler ne stress.
          </p>
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section">
        <div className="problem-container">
          <h2 className="problem-title">Ti suona familiare?</h2>
          <div className="problems-grid">
            <div className="problem-card">
              <span className="problem-icon">
                <SmileyMehIcon size={38} weight="regular" />
              </span>
              <p className="problem-text">
                <strong>Scoprire all'ultimo</strong><br/> 
                che hai comprato lo stesso regalo <br/>
                di qualcun altro?
              </p>
            </div>
            <div className="problem-card">
              <span className="problem-icon">
                <QuestionIcon size={38} weight="regular" />
              </span>
              <p className="problem-text">
                <strong>Non avere più idee</strong><br/> 
                per figli, amici o colleghi <br/>
                perché i gusti cambiano ogni mese?
              </p>
            </div>
            <div className="problem-card">
              <span className="problem-icon">
                <DeviceMobileIcon size={38} weight="regular" />
              </span>
              <p className="problem-text">
                <strong>Perderti tra screenshot e note</strong><br/> 
                cercando “quel messaggio” <br/>
                nella chat due giorni prima della festa?
              </p>
            </div>
            <div className="problem-card">
              <span className="problem-icon">
                <SmileyNervousIcon size={38} weight="regular" />
              </span>
              <p className="problem-text">
                <strong>Rovinare la sorpresa</strong><br/> 
                chiedendo apertamente cosa<br/>
                vorrebbero ricevere?
              </p>
            </div>
            <div className="problem-card">
              <span className="problem-icon">
                <CalendarXIcon size={38} weight="regular" />
              </span>
              <p className="problem-text">
                <strong>Dimenticare un compleanno</strong><br/> 
                perché nessuno del gruppo<br/>
                si è ricordato di avvisarti?
              </p>
            </div>
            <div className="problem-card">
              <span className="problem-icon">
                <GiftIcon size={38} weight="regular" />
              </span>
              <p className="problem-text">
                <strong>Non sapere cosa regalare</strong><br/> 
                a chi sembra avere già tutto<br/>
                (e non ti aiuta per niente)?
              </p>
            </div>
          </div>
          <div className="problem-solution">
            <p className="solution-text">
              <strong>
                Con CosaVorrei questi momenti diventano ricordi.
              </strong>{" "}
              Famiglie, amici e colleghi ritrovano per ogni evento i regali giusti in un unico posto.<br/>
              Semplice, ordinato e senza spoiler.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          <h2 className="features-title">
            Cosa rende speciale CosaVorrei
          </h2>
          <div className="features-grid">
            <div className="feature-card featured">
              <span className="feature-icon">
                <TreeIcon size={38} weight="regular" />
              </span>
              <h3 className="feature-title">Un gruppo, tutte le occasioni</h3>
              <p className="feature-description">
                Invita familiari, amici o colleghi e crea un evento per ogni 
                ricorrenza: Natale, compleanni, anniversari o iniziative aziendali. 
                Tutto nello stesso spazio.
              </p>
            </div>

            <div className="feature-card featured">
              <span className="feature-icon">
                <GiftIcon size={38} weight="regular" />
              </span>
              <h3 className="feature-title">Liste per ogni persona</h3>
              <p className="feature-description">
                Ogni membro aggiunge i propri desideri; chi gestisce bambini 
                o persone senza accesso può aggiornare le loro liste. 
                Nessun regalo dimenticato.
              </p>
            </div>

            <div className="feature-card">
              <span className="feature-icon">
                <LockIcon size={38} weight="regular" />
              </span>
              <h3 className="feature-title">Prenotazioni invisibili</h3>
              <p className="feature-description">
                Prenoti in segreto: il festeggiato può sapere che qualcuno si occuperà del regalo,
o restare all’oscuro fino al giorno della festa.
              </p>
            </div>

            <div className="feature-card">
              <span className="feature-icon">
                <TargetIcon size={38} weight="regular" />
              </span>
              <h3 className="feature-title">Zero regali doppi</h3>
              <p className="feature-description">
                Nessun rischio di doppioni: vedi subito se un regalo è già stato
                prenotato e puoi orientarti verso un'alternativa.
              </p>
            </div>

            <div className="feature-card">
              <span className="feature-icon">
                <DeviceMobileIcon size={38} weight="regular" />
              </span>
              <h3 className="feature-title">Aggiornato ovunque</h3>
              <p className="feature-description">
                Le liste si sincronizzano in tempo reale e funzionano su
                smartphone, tablet e computer senza installare nulla.
              </p>
            </div>

            <div className="feature-card">
              <span className="feature-icon">
                <ConfettiIcon size={38} weight="regular" />
              </span>
              <h3 className="feature-title">Eventi personalizzati</h3>
              <p className="feature-description">
                Natale, Epifania, Pasqua, viaggi tra amici, eventi aziendali:
                ogni occasione ha le sue liste, i suoi partecipanti e le sue
                regole.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="how-it-works">
        <div className="how-it-works-container">
          <h2 className="how-it-works-title">Come funziona la magia</h2>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3 className="step-title">Crea il gruppo</h3>
              <p className="step-description">
                Invita le persone che ami e riuniscile nello stesso spazio.
In pochi minuti avrete il gruppo pronto per ogni occasione.
              </p>
            </div>

            <div className="step-card">
              <div className="step-number">2</div>
              <h3 className="step-title">Aggiungi gli eventi</h3>
              <p className="step-description">
                Natale, compleanni, anniversari o sorprese improvvisate:
definisci gli eventi e scegli chi partecipa a ciascuno.
              </p>
            </div>

            <div className="step-card">
              <div className="step-number">3</div>
              <h3 className="step-title">Costruite le liste</h3>
              <p className="step-description">
                Ognuno aggiunge i propri desideri, anche per i più piccoli.
Le liste restano aggiornate e visibili solo al gruppo.
              </p>
            </div>

            <div className="step-card">
              <div className="step-number">4</div>
              <h3 className="step-title">Prenotate in segreto</h3>
              <p className="step-description">
                Chi sceglie un regalo lo prenota in un clic:
niente doppioni, nessuna chat confusa, solo sorprese riuscite.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <h2 className="cta-title">Organizza i prossimi regali senza stress</h2>
          <p className="cta-subtitle">
            Riunisci famiglia, amici o colleghi, pianifica ogni evento e
            assicurati che ognuno riceva davvero ciò che desidera.
          </p>
          <Link to="/create-group" className="cta-button">
            Crea il tuo gruppo
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default HomePage;
